'use client';

import { favoriteServiceAddToFavorites, favoriteServiceRemoveFromFavorites } from '@dobrozem/sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startTransition, useEffect, useState } from 'react';

import { clientSdk } from '@/lib/sdk';

type FavoritesState = string[];

interface UseFavoritesReturn {
  addFavorite: (itemId: string) => void;
  favorites: FavoritesState;
  removeFavorite: (itemId: string) => void;
}

export const useFavorites = (): UseFavoritesReturn => {
  const queryClient = useQueryClient();

  // Ініціалізуємо стан з перевіркою на доступність localStorage
  const [favorites, setFavorites] = useState<FavoritesState>(() => {
    if (typeof window === 'undefined') return []; // SSR: повертаємо порожній масив
    const savedFavorites = localStorage.getItem('favorites');

    return savedFavorites ? (JSON.parse(savedFavorites) as FavoritesState) : [];
  });

  const getFavoritesFromStorage = (): FavoritesState => {
    if (typeof window === 'undefined') return []; // Перевірка середовища
    const savedFavorites = localStorage.getItem('favorites');

    return savedFavorites ? (JSON.parse(savedFavorites) as FavoritesState) : [];
  };

  const updateFavoritesInStorage = (updatedFavorites: FavoritesState) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
        setFavorites(updatedFavorites);
      } catch (error) {
        console.error('Error updating favorites in localStorage:', error);
      }
    }
  };

  const addToFavoriteMutation = useMutation({
    mutationFn: favoriteServiceAddToFavorites<false>,
    onSuccess: (_, variables) => {
      const currentFavorites = getFavoritesFromStorage();

      const itemId = variables.body.advertisementId;

      if (!currentFavorites.includes(itemId)) {
        const updatedFavorites = [...currentFavorites, itemId];

        updateFavoritesInStorage(updatedFavorites);
      }

      void queryClient.invalidateQueries(); // Інвалідація кешу
    },
  });

  const removeFromFavoriteMutation = useMutation({
    mutationFn: favoriteServiceRemoveFromFavorites<false>,
    onError: (error) => {
      console.error('Error removing from favorites:', error);
    },
    onSuccess: (_, variables) => {
      const currentFavorites = getFavoritesFromStorage();

      const itemId = variables.body.advertisementId;

      const updatedFavorites = currentFavorites.filter((id) => id !== itemId);

      updateFavoritesInStorage(updatedFavorites);

      void queryClient.invalidateQueries(); // Інвалідація кешу
    },
  });

  const addFavorite = (itemId: string) => {
    const currentFavorites = getFavoritesFromStorage();

    if (!currentFavorites.includes(itemId)) {
      addToFavoriteMutation.mutate({
        body: { advertisementId: itemId },
        client: clientSdk,
      });
    }
  };

  const removeFavorite = (itemId: string): void => {
    removeFromFavoriteMutation.mutate({
      body: { advertisementId: itemId },
      client: clientSdk,
    });
  };

  const updateFavoritesFromStorage = () => {
    const savedFavorites = localStorage.getItem('favorites');

    setFavorites(savedFavorites ? (JSON.parse(savedFavorites) as FavoritesState) : []);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return; // Перевірка середовища

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'favorites') {
        updateFavoritesFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    startTransition(() => {
      updateFavoritesFromStorage(); // Ініціалізація під час першого рендеру
    });

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return { addFavorite, favorites, removeFavorite };
};
